import { useState } from 'react';

export interface SprintFormData {
  name: string;
  goal: string;
  status: 'future' | 'active' | 'closed';
  start_date: string;
  end_date: string;
  value: number;
}

interface SprintModalProps {
  sprint?: Partial<SprintFormData> & { id?: string };
  onClose: () => void;
  onSave: (data: SprintFormData) => void;
}

export default function SprintModal({ sprint, onClose, onSave }: SprintModalProps) {
  const [form, setForm] = useState<SprintFormData>({
    name: sprint?.name || '',
    goal: sprint?.goal || '',
    status: sprint?.status || 'future',
    start_date: sprint?.start_date || '',
    end_date: sprint?.end_date || '',
    value: sprint?.value ?? 0,
  });

  const set = (field: keyof SprintFormData, value: string | number) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-slate-800 font-semibold text-base">
            {sprint?.id ? 'Editar Sprint' : 'Nova Sprint'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Nome da Sprint *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Ex: Sprint 1, Sprint de Lançamento..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Objetivo</label>
            <textarea
              value={form.goal}
              onChange={(e) => set('goal', e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Objetivo desta sprint..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Valor da Sprint
              <span className="ml-1 text-slate-400 font-normal">(será lançado como pendente no financeiro do cliente)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.value || ''}
                onChange={(e) => set('value', parseFloat(e.target.value) || 0)}
                placeholder="0,00"
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
            >
              <option value="future">Sprint Futura</option>
              <option value="active">Sprint Atual (Ativa)</option>
              <option value="closed">Sprint Encerrada</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Início</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Término</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => set('end_date', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button type="submit" className="px-5 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
              {sprint?.id ? 'Salvar' : 'Criar Sprint'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
